/**
 * AngularJS Upgraded Components
 *
 * This file contains wrapper directives that allow AngularJS components
 * to be used in Angular templates.
 *
 * Usage in Angular template:
 * ```html
 * <legacy-loader [loaderId]="'main'" [hidden]="false"></legacy-loader>
 * <legacy-spinner></legacy-spinner>
 * ```
 *
 * Add these directives to SharedModule declarations.
 */
import { Directive, ElementRef, Inject, Injector, Input } from '@angular/core';
import { UpgradeComponent } from '@angular/upgrade/static';

// ============================================
// Upgraded AngularJS Directives
// ============================================
// Uncomment as needed during migration

/**
 * Wrapper for AngularJS 'loader' directive
 *
 * Usage:
 * ```html
 * <legacy-loader loaderId="main" hidden="false"></legacy-loader>
 * ```
 */
// @Directive({ selector: 'legacy-loader' })
// export class LegacyLoaderDirective extends UpgradeComponent {
//   @Input() loaderId: string;
//   @Input() hidden: string;
//
//   constructor(elementRef: ElementRef, injector: Injector) {
//     super('loader', elementRef, injector);
//   }
// }

/**
 * Wrapper for AngularJS 'spinner' directive
 *
 * NOTE: Cannot be used - spinner directive uses `replace: true` which is
 * not supported by UpgradeComponent.
 *
 * Usage:
 * ```html
 * <legacy-spinner [spinnerHide]="isLoading"></legacy-spinner>
 * ```
 */
// @Directive({ selector: 'legacy-spinner' })
// export class LegacySpinnerDirective extends UpgradeComponent {
//   @Input() spinnerHide?: boolean;
//
//   constructor(
//     @Inject(ElementRef) elementRef: ElementRef,
//     @Inject(Injector) injector: Injector
//   ) {
//     super('spinner', elementRef, injector);
//   }
// }

/**
 * Wrapper for AngularJS 'acmeAlert' component
 *
 * Usage:
 * ```html
 * <legacy-acme-alert [type]="'warning'">Alert message here</legacy-acme-alert>
 * ```
 */
@Directive({ selector: 'legacy-acme-alert' })
export class LegacyAcmeAlertDirective extends UpgradeComponent {
  @Input() type?: 'default' | 'info' | 'warning' | 'error' | 'success';
  @Input() icon?: string;

  constructor(
    @Inject(ElementRef) elementRef: ElementRef,
    @Inject(Injector) injector: Injector
  ) {
    super('acmeAlert', elementRef, injector);
  }
}

/**
 * Wrapper for AngularJS 'dataGrid' directive
 *
 * Usage:
 * ```html
 * <legacy-data-grid [options]="gridOptions"></legacy-data-grid>
 * ```
 */
// @Directive({ selector: 'legacy-data-grid' })
// export class LegacyDataGridDirective extends UpgradeComponent {
//   @Input() options: any;
//
//   constructor(elementRef: ElementRef, injector: Injector) {
//     super('dataGrid', elementRef, injector);
//   }
// }

/**
 * All upgraded AngularJS component directives
 * Add to SharedModule declarations
 */
export const AJS_UPGRADED_COMPONENTS = [
  // LegacyLoaderDirective,
  // LegacySpinnerDirective, // Not supported - uses replace: true
  LegacyAcmeAlertDirective,
  // LegacyDataGridDirective,
];

/**
 * Example: How to upgrade an AngularJS directive for use in Angular
 *
 * 1. Create wrapper directive:
 * ```typescript
 * @Directive({ selector: 'legacy-my-directive' })
 * export class LegacyMyDirective extends UpgradeComponent {
 *   @Input() myInput: string;
 *   @Output() myOutput = new EventEmitter<any>();
 *
 *   constructor(elementRef: ElementRef, injector: Injector) {
 *     // 'myDirective' is the AngularJS directive name (camelCase)
 *     super('myDirective', elementRef, injector);
 *   }
 * }
 * ```
 *
 * 2. Add to SharedModule declarations
 *
 * 3. Use in Angular template:
 * ```html
 * <legacy-my-directive
 *   [myInput]="value"
 *   (myOutput)="handleOutput($event)">
 * </legacy-my-directive>
 * ```
 */

